import { Form } from 'antd';
import type { NamePath } from 'rc-field-form/es/interface';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import { useStore } from 'zustand/react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { formatDateWithoutHour } from 'app/utils/dates';

import { inventoryStore } from '../../InventoryStore';
import type {
  DuplicationModeData,
  FormPackageBooking,
  OpeningErrors,
  OpeningStatus,
} from '../../type';

import { EditableCell } from './_components/EditableCell';
import { ReadOnlyCell } from './_components/ReadOnlyCell';

type OverviewCellProps = {
  nameField?: NamePath;
  date: Date;
  status?: OpeningErrors | OpeningStatus.PUBLISHED;
  datePlanning: FormPackageBooking;
  getDescriptionValue?: (
    datePlanning: FormPackageBooking,
    date: string
  ) => number | string | ReactNode;
  isEditable: boolean;
  getValue?: (datePlanning: FormPackageBooking, date: string) => number;
  packageId?: number;
  name?: string;
};

export const OverviewCell = ({
  getValue,
  getDescriptionValue,
  name,
  date,
  nameField,
  isEditable = true,
  status,
  datePlanning,
  packageId,
}: OverviewCellProps) => {
  const formattedDate = formatDateWithoutHour(date);
  const form = Form.useFormInstance();
  const { hotelInfo } = useContext(HotelContext);
  const { packages, getExperiencesForPackage, hasAtLeastOneDayPackage } =
    useContext(LegacyHotelContext);

  const handleDuplicate = useStore(
    inventoryStore,
    (state) => state.handleDuplicate
  );

  if (isEditable) {
    const tryDuplicate = (modeData: DuplicationModeData) => {
      handleDuplicate &&
        name &&
        nameField &&
        handleDuplicate(
          {
            property: name,
            value: form.getFieldValue(nameField),
            day: date,
            modeData,
            packageId,
            nameField,
          },
          packages || [],
          getExperiencesForPackage,
          hotelInfo?.roomCount || 0,
          hasAtLeastOneDayPackage
        );
    };

    return (
      <EditableCell
        date={date}
        nameField={nameField}
        name={name}
        hasDuplication={true}
        descriptionValue={getDescriptionValue?.(datePlanning, formattedDate)}
        handleDuplicate={tryDuplicate}
      />
    );
  }

  if (getValue) {
    return (
      <ReadOnlyCell
        value={getValue(datePlanning, formattedDate)}
        descriptionValue={getDescriptionValue?.(datePlanning, formattedDate)}
      />
    );
  }

  return null;
};
